import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { crmStore, useCrm } from '../store';
import { Card, StatutBadge } from '../ui';
import { formatDateFR, jLabel } from '../utils';
import { Bell, CheckCircle2, Calendar, RotateCcw, AlarmClock, Save, BellPlus, X, Trash2, Pencil, History, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../auth/AuthContext';
import HistoryRelancesModal from './HistoryRelancesModal';
import SharedReminderModal from './ReminderModal';
import { RelanceStats, RelanceFilterChips } from './RelancePageLayout';
import { useNotifications } from '../notifications/NotificationsContext';
import { useDemoMode } from '../demoMode';



const SETTINGS_KEY = 'crm.relances.inactivityDays';
const CUSTOM_KEY = 'crm.relances.customReminders';
const TASK_MAP_KEY = 'crm.relances.reminderTaskMap';
const DEFAULT_DAYS = 3;
const TERMINAL_STATUSES = new Set(['Refusée', 'Réalisée', 'Payé & Livré', 'Expirée', 'Annulée', 'Brouillon abandonné']);

type CustomReminder = { id: string; date: string; note: string; done?: boolean };
type CustomMap = Record<string, CustomReminder[]>;

function loadCustom(): CustomMap {
  try { return JSON.parse(localStorage.getItem(CUSTOM_KEY) || '{}'); } catch { return {}; }
}
function saveCustom(m: CustomMap) {
  localStorage.setItem(CUSTOM_KEY, JSON.stringify(m));
}

// Mapping local reminder.id -> prestations_taches.id (uuid)
function loadTaskMap(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(TASK_MAP_KEY) || '{}'); } catch { return {}; }
}
function saveTaskMap(m: Record<string, string>) {
  localStorage.setItem(TASK_MAP_KEY, JSON.stringify(m));
}

// Résout l'UUID DB d'une prestation à partir de son numero (p.id côté CRM) ou de son UUID direct.
async function resolvePrestationUuid(idOrNumero: string): Promise<string | null> {
  const byNum = await supabase.from('prestations').select('id').eq('numero', idOrNumero).maybeSingle();
  if (byNum.data?.id) return byNum.data.id as string;
  const byId = await supabase.from('prestations').select('id').eq('id', idOrNumero).maybeSingle();
  return (byId.data?.id as string) || null;
}


function loadDays(): number {
  const raw = localStorage.getItem(SETTINGS_KEY);
  const n = raw ? parseInt(raw, 10) : DEFAULT_DAYS;
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_DAYS;
}

function daysSince(iso: string): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.floor((Date.now() - t) / 86400000);
}

type HistoryEntry = { id: string; date: string; note?: string; auteur?: string };
type HistoryMap = Record<string, HistoryEntry[]>;

export default function RelancesClientSection() {
  const data = useCrm();
  const [demo] = useDemoMode();
  const { user } = useAuth();
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const [filter, setFilter] = useState<'all' | 'todo' | 'urgent' | 'done' | 'inactive' | 'rappels'>('todo');
  const [days, setDays] = useState<number>(loadDays);
  const [draftDays, setDraftDays] = useState<number>(loadDays);
  const [customMap, setCustomMap] = useState<CustomMap>(loadCustom);
  const [editing, setEditing] = useState<{ prestId: string; reminder?: CustomReminder } | null>(null);
  const [doneModal, setDoneModal] = useState<{ prestId: string } | null>(null);
  const [historyModal, setHistoryModal] = useState<{ prestId: string } | null>(null);
  const [historyMap, setHistoryMap] = useState<HistoryMap>({});
  const [historyVersion, setHistoryVersion] = useState(0);
  // Date du dernier édit de la proposition liée à chaque prestation (clé : id CRM).
  // Permet de sortir un dossier de l'état « inactif » dès qu'on édite sa propal
  // (cf. demande client : on attend la prochaine relance/action pour le ré-flagger).
  const [propEditMap, setPropEditMap] = useState<Record<string, string>>({});
  const doneMap = data.relanceNotes ?? {};

  // Charge l'historique partagé depuis Supabase
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: rows, error } = await supabase
        .from('relances_historique')
        .select('id, prestation_id, date, note, auteur_email')
        .order('date', { ascending: false });
      if (cancelled || error || !rows) return;
      const map: HistoryMap = {};
      for (const r of rows as any[]) {
        const key = r.prestation_id as string;
        (map[key] ||= []).push({ id: r.id, date: r.date, note: r.note || undefined, auteur: r.auteur_email || undefined });
      }
      setHistoryMap(map);
    })();
    return () => { cancelled = true; };
  }, [historyVersion]);

  // Charge la date du dernier édit de propal ET contrat par prestation (mapping uuid → id CRM).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: prestRows }, { data: propRows }, { data: contratRows }] = await Promise.all([
        supabase.from('prestations').select('id, numero, updated_at'),
        supabase.from('propositions').select('prestation_id, updated_at').not('prestation_id', 'is', null),
        supabase.from('contrats').select('prestation_id, updated_at').not('prestation_id', 'is', null).is('deleted_at', null),
      ]);
      if (cancelled) return;
      const uuidToCrm = new Map<string, string>();
      for (const r of (prestRows || []) as any[]) uuidToCrm.set(r.id, r.numero || r.id);
      const map: Record<string, string> = {};
      const acc = (prestUuid: string, iso?: string | null) => {
        const crmId = uuidToCrm.get(prestUuid);
        if (!crmId || !iso) return;
        if (!map[crmId] || iso > map[crmId]) map[crmId] = iso;
      };
      for (const r of (prestRows || []) as any[]) acc(r.id, r.updated_at);
      for (const r of (propRows || []) as any[]) acc(r.prestation_id, r.updated_at);
      for (const r of (contratRows || []) as any[]) acc(r.prestation_id, r.updated_at);
      setPropEditMap(map);
    })();
    return () => { cancelled = true; };
  }, [data.prest.length, data.prest]);


  // Référence d'inactivité = dernière modification réelle du dossier / propal / contrat.
  const lastActivityIsoFor = (p: any, propMap: Record<string, string>) => {
    const candidates = [p.updatedAt, propMap[p.id], p.dateCreation].filter(Boolean) as string[];
    if (!candidates.length) return p.dateCreation;
    return candidates.reduce((a, b) => (a > b ? a : b));
  };

  // Liste de base : un dossier n'apparaît dans Relances que s'il y a quelque chose à faire :
  //  - inactif depuis ≥ seuil (sans modif récente sur prestation/propal/contrat)
  //  - OU déjà marqué "relancé" (trace + possibilité de rouvrir)
  //  - OU avec un rappel personnalisé en cours
  // Dès qu'on modifie le statut ou un champ → updated_at = now() → le dossier sort de la liste
  // jusqu'à atteinte du seuil d'inactivité.
  const all = useMemo(
    () => data.prest.filter((p) => {
      if (TERMINAL_STATUSES.has(p.statut)) return false;
      if (!demo && !/relance|propal|demande/i.test(p.statut)) return false;
      if (doneMap[p.id]) return true;
      if ((customMap[p.id] || []).some((r) => !r.done)) return true;
      const ref = lastActivityIsoFor(p, propEditMap);
      return daysSince(ref) >= days;
    }),
    [data.prest, propEditMap, demo, doneMap, customMap, days],
  );

  const lastActivityIso = (p: typeof all[number]) => lastActivityIsoFor(p, propEditMap);

  const isInactive = (p: typeof all[number]) => {
    if (doneMap[p.id]) return false;
    return daysSince(lastActivityIso(p)) >= days;
  };


  const todayIso = new Date().toISOString().slice(0, 10);
  const hasDueReminder = (id: string) => (customMap[id] || []).some((r) => !r.done && r.date <= todayIso);
  const hasAnyReminder = (id: string) => (customMap[id] || []).some((r) => !r.done);

  const filtered = useMemo(() => all.filter((p) => {
    const done = Boolean(doneMap[p.id]);
    if (filter === 'done') return done;
    if (filter === 'todo') return !done;
    if (filter === 'inactive') return isInactive(p);
    if (filter === 'rappels') return hasAnyReminder(p.id);
    if (filter === 'urgent') {
      const d = new Date(p.dateEvent).getTime() - Date.now();
      return d / 86400000 <= 30 && !done;
    }
    return true;
  }), [all, filter, doneMap, days, customMap, propEditMap]);

  const counts = {
    total: all.length,
    done: all.filter((p) => Boolean(doneMap[p.id])).length,
    todo: all.filter((p) => !doneMap[p.id]).length,
    inactive: all.filter(isInactive).length,
    rappels: all.filter((p) => hasAnyReminder(p.id)).length,
  };


  // Auto-création de notifications cloche pour les prestations inactives (1 fois par dossier)
  // + suppression des notifs des dossiers qui ne sont plus inactifs (activité récente).
  useEffect(() => {
    if (!user) return;
    const inactives = all.filter(isInactive);
    const inactiveIds = new Set(inactives.map((p) => p.id));
    let cancelled = false;
    (async () => {
      const { data: existing } = await supabase
        .from('notifications')
        .select('id, metadata, message, type')
        .eq('user_id', user.id)
        .or('type.eq.action_a_suivre,type.eq.prestation_a_relancer');
      if (cancelled) return;
      const existingByRef = new Map<string, any[]>();
      for (const n of (existing || []) as any[]) {
        // Ne garder que les notifs de source "relance_dossier" (ou legacy prestation_a_relancer)
        const src = n?.metadata?.source;
        if (n.type === 'action_a_suivre' && src && src !== 'relance_dossier') continue;
        const ref = n?.metadata?.prestation_ref;
        if (ref) {
          const list = existingByRef.get(ref) || [];
          list.push(n);
          existingByRef.set(ref, list);
        }
      }
      // 0. Suppression des notifs pour les dossiers qui ne sont PLUS inactifs
      //    (compteur d'inactivité remis à 0 par une modif de la prestation/propal/contrat).
      const staleIds: string[] = [];
      for (const [ref, rows] of existingByRef.entries()) {
        if (!inactiveIds.has(ref)) staleIds.push(...rows.map((n) => n.id));
      }
      if (staleIds.length) {
        await supabase.from('notifications').delete().in('id', staleIds);
      }
      // 1. Mise à jour des notifs existantes si le nombre de jours d'inactivité a changé
      for (const p of inactives) {
        const ex = existingByRef.get(p.id)?.[0];
        if (!ex) continue;
        const since = daysSince(lastActivityIso(p));
        if ((ex.metadata?.inactif_depuis ?? -1) !== since) {
          await supabase.from('notifications').update({
            message: `${p.libelle || 'Dossier'} — sans action depuis ${since} jours (seuil ${days}j).`,
            metadata: { ...(ex.metadata || {}), source: 'relance_dossier', prestation_ref: p.id, seuil_jours: days, inactif_depuis: since },
          }).eq('id', ex.id);
        }
      }
      // 2. Création pour les dossiers sans notif active
      const toInsert = inactives.filter((p) => !existingByRef.has(p.id));
      if (!toInsert.length) return;
      const rows = toInsert.map((p) => {
        const since = daysSince(lastActivityIso(p));
        return {
          user_id: user.id,
          type: 'action_a_suivre' as const,
          titre: `Relance à effectuer : ${p.client || 'Client'}`,
          message: `${p.libelle || 'Dossier'} — sans action depuis ${since} jours (seuil ${days}j).`,
          lien: '/crm-pro-evenementiel/relances',
          metadata: { source: 'relance_dossier', prestation_ref: p.id, seuil_jours: days, inactif_depuis: since },
        };
      });
      await supabase.from('notifications').insert(rows);
    })();
    return () => { cancelled = true; };
  }, [all, days, user, doneMap]);


  // Alerte auto : propositions encore en "Brouillon" au-delà du seuil
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data: brouillons } = await supabase
        .from('propositions')
        .select('id, numero, client_nom, updated_at, prestation_id')
        .eq('statut', 'Brouillon');
      if (cancelled || !brouillons) return;
      const stuck = (brouillons as any[]).filter((p) => {
        if (!p.updated_at) return false;
        return daysSince(p.updated_at) >= days;
      });
      if (!stuck.length) return;
      const { data: existing } = await supabase
        .from('notifications')
        .select('id, entity_id, metadata, type')
        .eq('user_id', user.id)
        .or('type.eq.action_a_suivre,type.eq.prestation_a_relancer')
        .eq('entity_type', 'proposition');
      if (cancelled) return;
      const existingByProp = new Map<string, any>();
      for (const n of (existing || []) as any[]) {
        const src = n?.metadata?.source;
        if (n.type === 'action_a_suivre' && src && src !== 'relance_proposition') continue;
        if (n.entity_id) existingByProp.set(n.entity_id, n);
      }
      const toInsert: any[] = [];
      for (const p of stuck) {
        const since = daysSince(p.updated_at);
        const ex = existingByProp.get(p.id);
        const message = `Proposition ${p.numero || ''} — ${p.client_nom || 'Client'} toujours en Brouillon depuis ${since}\u00A0jours (seuil ${days}\u00A0j).`;
        if (ex) {
          if ((ex.metadata?.inactif_depuis ?? -1) !== since) {
            await supabase.from('notifications').update({
              message,
              metadata: { ...(ex.metadata || {}), source: 'relance_proposition', inactif_depuis: since, seuil_jours: days },
            }).eq('id', ex.id);
          }
        } else {
          toInsert.push({
            user_id: user.id,
            type: 'action_a_suivre' as const,
            titre: `Propal en brouillon : ${p.client_nom || 'Client'}`,
            message,
            lien: '/crm-pro-evenementiel/propositions-contrats?tab=propositions',
            prestation_id: p.prestation_id || null,
            entity_type: 'proposition',
            entity_id: p.id,
            metadata: { source: 'relance_proposition', proposition_brouillon: true, seuil_jours: days, inactif_depuis: since },
          });
        }
      }
      if (toInsert.length) await supabase.from('notifications').insert(toInsert);
    })();
    return () => { cancelled = true; };
  }, [days, user]);

  const reopenRelance = (id: string) => {
    const next = { ...doneMap };
    delete next[id];
    crmStore.update({ relanceNotes: next });
    toast.success('Relance rebasculée en attente');
  };

  const markRelanceDone = async (id: string, note: string) => {
    const today = new Date().toISOString().slice(0, 10);
    const nextNotes = { ...doneMap, [id]: today };
    crmStore.update({ relanceNotes: nextNotes });
    const uuid = await resolvePrestationUuid(id);
    if (!uuid) {
      toast.error('Prestation introuvable côté base');
      return;
    }
    const { error } = await supabase.from('relances_historique').insert({
      prestation_id: uuid,
      date: today,
      note: note.trim() || null,
      auteur_user_id: user?.id ?? null,
      auteur_email: user?.email ?? null,
    });
    if (error) {
      toast.error('Erreur enregistrement historique');
    } else {
      toast.success('Relance enregistrée dans l\u2019historique');
      setHistoryVersion((v) => v + 1);
    }
  };

  const saveDays = () => {
    const n = Math.max(1, Math.min(365, Math.floor(draftDays || DEFAULT_DAYS)));
    localStorage.setItem(SETTINGS_KEY, String(n));
    setDays(n);
    setDraftDays(n);
    toast.success(`Alerte d'inactivité réglée sur ${n}\u00A0jour${n > 1 ? 's' : ''}`);
  };

  // Synchronise un rappel personnalisé avec la table prestations_taches (création/mise à jour)
  const syncReminderTask = async (prestId: string, rem: CustomReminder) => {
    const uuid = await resolvePrestationUuid(prestId);
    if (!uuid) return;
    const taskMap = loadTaskMap();
    const existingTaskId = taskMap[rem.id];
    const responsable = user?.email || user?.user_metadata?.full_name || null;
    const libelle = `Relance : ${rem.note?.trim() || 'rappel programmé'}`;
    if (existingTaskId) {
      await supabase.from('prestations_taches').update({
        libelle,
        date_limite: rem.date,
        fait: !!rem.done,
      }).eq('id', existingTaskId);
    } else {
      const { data: inserted } = await supabase.from('prestations_taches').insert({
        prestation_id: uuid,
        libelle,
        categorie: 'Relance',
        responsable,
        date_limite: rem.date,
        fait: !!rem.done,
        created_by: user?.id ?? null,
      }).select('id').single();
      if (inserted?.id) {
        taskMap[rem.id] = inserted.id;
        saveTaskMap(taskMap);
      }
    }
  };

  const removeReminderTask = async (remId: string) => {
    const taskMap = loadTaskMap();
    const taskId = taskMap[remId];
    if (!taskId) return;
    await supabase.from('prestations_taches').delete().eq('id', taskId);
    delete taskMap[remId];
    saveTaskMap(taskMap);
  };

  const upsertReminder = (prestId: string, rem: CustomReminder) => {
    const list = customMap[prestId] ? [...customMap[prestId]] : [];
    const idx = list.findIndex((r) => r.id === rem.id);
    if (idx >= 0) list[idx] = rem; else list.push(rem);
    const next = { ...customMap, [prestId]: list };
    setCustomMap(next); saveCustom(next);
    void syncReminderTask(prestId, rem);
    toast.success(idx >= 0 ? 'Rappel mis à jour' : 'Rappel ajouté');
  };
  const deleteReminder = (prestId: string, remId: string) => {
    const list = (customMap[prestId] || []).filter((r) => r.id !== remId);
    const next = { ...customMap, [prestId]: list };
    setCustomMap(next); saveCustom(next);
    void removeReminderTask(remId);
    toast.success('Rappel supprimé');
  };
  const toggleReminderDone = (prestId: string, remId: string) => {
    const list = (customMap[prestId] || []).map((r) => r.id === remId ? { ...r, done: !r.done } : r);
    const next = { ...customMap, [prestId]: list };
    setCustomMap(next); saveCustom(next);
    const updated = list.find((r) => r.id === remId);
    if (updated) void syncReminderTask(prestId, updated);
  };

  // Notification cloche pour rappels personnalisés arrivés à échéance
  useEffect(() => {
    if (!user) return;
    const due: { p: typeof all[number]; r: CustomReminder }[] = [];
    all.forEach((p) => (customMap[p.id] || []).forEach((r) => {
      if (!r.done && r.date <= todayIso) due.push({ p, r });
    }));
    if (!due.length) return;
    let cancelled = false;
    (async () => {
      const { data: existing } = await supabase
        .from('notifications').select('metadata')
        .eq('user_id', user.id).eq('type', 'rappel_personnalise');
      if (cancelled) return;
      const alreadySent = new Set<string>((existing || []).map((n: any) => n?.metadata?.reminder_id).filter(Boolean));
      const rows = due.filter(({ r }) => !alreadySent.has(r.id)).map(({ p, r }) => ({
        user_id: user.id,
        type: 'rappel_personnalise' as const,
        titre: `Rappel : ${p.client || 'Client'}`,
        message: `${r.note || 'Rappel programmé'} — échéance ${formatDateFR(r.date)} (${p.libelle || 'dossier'}).`,
        lien: '/crm-pro-evenementiel/relances',
        metadata: { prestation_ref: p.id, reminder_id: r.id, date: r.date },
      }));
      if (rows.length) await supabase.from('notifications').insert(rows);
    })();
    return () => { cancelled = true; };
  }, [customMap, all, user, todayIso]);


  return (
    <div>



      <RelanceStats items={[
        { label: 'Total dossiers', value: counts.total, tone: 'amber' },
        { label: 'À relancer', value: counts.todo, tone: 'sky' },
        { label: `Inactifs ≥\u00A0${days}\u00A0j`, value: counts.inactive, tone: 'rose' },
        { label: 'Rappels en cours', value: counts.rappels, tone: 'violet' },
        { label: 'Relancés', value: counts.done, tone: 'emerald' },
      ]} />

      {/* Filtres — barre commune en haut, jamais en bas */}
      <RelanceFilterChips items={[
        { key: 'all', label: 'Toutes', count: counts.total, active: filter === 'all', onClick: () => setFilter('all'), tone: 'amber' },
        { key: 'todo', label: 'À relancer', count: counts.todo, active: filter === 'todo', onClick: () => setFilter('todo'), tone: 'sky' },
        { key: 'inactive', label: `Inactives ≥\u00A0${days}\u00A0j`, count: counts.inactive, active: filter === 'inactive', onClick: () => setFilter('inactive'), tone: 'rose' },
        { key: 'rappels', label: 'Mes rappels', count: counts.rappels, active: filter === 'rappels', onClick: () => setFilter('rappels'), tone: 'violet' },
        { key: 'urgent', label: 'Urgentes J-30', active: filter === 'urgent', onClick: () => setFilter('urgent'), tone: 'rose' },
        { key: 'done', label: 'Relancées', count: counts.done, active: filter === 'done', onClick: () => setFilter('done'), tone: 'emerald' },
      ]} />

      {/* Réglage seuil inactivité */}
      <Card className="p-3 mb-5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-amber-400">
          <AlarmClock size={16} strokeWidth={1.5} />
          <span className="text-sm font-medium">Alerte automatique d'inactivité</span>
        </div>
        <span className="text-xs text-slate-400">Notifier après</span>
        <input
          type="number"
          min={1}
          max={365}
          value={draftDays}
          onChange={(e) => setDraftDays(parseInt(e.target.value, 10) || 0)}
          className="w-16 px-2 py-1 rounded-md bg-slate-900 border border-slate-700 text-sm text-slate-200 focus:outline-none focus:border-amber-500/50"
        />
        <span className="text-xs text-slate-400">jour(s) sans action sur le dossier</span>
        <button
          onClick={saveDays}
          disabled={draftDays === days}
          className="ml-auto px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 border bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Save size={12} strokeWidth={1.5} /> Enregistrer
        </button>
      </Card>


      <div className="space-y-2">
        {filtered.map((p) => {
          const doneAt = doneMap[p.id];
          const inactDays = daysSince(lastActivityIso(p));
          const inactive = !doneAt && inactDays >= days;
          const reminders = (customMap[p.id] || []).slice().sort((a, b) => a.date.localeCompare(b.date));
          const due = reminders.some((r) => !r.done && r.date <= todayIso);
          return (
            <Card key={p.id} className={`p-4 hover:border-slate-700 transition-colors ${due ? 'border-violet-500/40' : inactive ? 'border-rose-500/40' : ''}`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${doneAt ? 'bg-emerald-500/10' : due ? 'bg-violet-500/10' : inactive ? 'bg-rose-500/10' : 'bg-amber-500/10'}`}>
                  {doneAt ? (
                    <CheckCircle2 size={16} className="text-emerald-400" strokeWidth={1.5} />
                  ) : due || inactive ? (
                    <Bell size={16} className={due ? 'text-violet-300' : 'text-rose-400'} strokeWidth={1.5} />
                  ) : (
                    <FileText size={16} className="text-amber-400" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={`/crm-pro-evenementiel/prestations?id=${encodeURIComponent(p.id)}`}
                      className="text-sm text-slate-200 font-medium hover:text-amber-300 hover:underline"
                      title="Ouvrir le dossier"
                    >
                      {p.client}
                    </Link>
                    {doneAt ? (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 rounded">Relancée le {formatDateFR(doneAt, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                    ) : inactive ? (
                      <span className="text-[10px] px-1.5 py-0.5 bg-rose-500/15 text-rose-300 rounded flex items-center gap-1">
                        <AlarmClock size={10} strokeWidth={1.5} /> Inactif depuis {inactDays}{'\u00A0'}j
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 text-amber-400 rounded">À traiter</span>
                    )}
                    {due && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-violet-500/15 text-violet-300 rounded flex items-center gap-1">
                        <BellPlus size={10} strokeWidth={1.5} /> Rappel dû
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
                    <span>{p.libelle}</span>
                    <span>·</span>
                    <span>{p.lieu || 'Lieu ?'}</span>
                    <span>·</span>
                    <StatutBadge statut={p.statut} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="px-1.5 py-0.5 bg-sky-500/10 text-sky-400 rounded">{jLabel(p.dateEvent)}</span>
                    <span className="flex items-center gap-1"><Calendar size={10} strokeWidth={1.5} /> {formatDateFR(p.dateCreation, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <div className="text-xs text-slate-400 flex items-center gap-1.5">
                    <Calendar size={11} strokeWidth={1.5} /> {formatDateFR(p.dateEvent)}
                  </div>
                  <div className="flex gap-1.5 flex-wrap justify-end">
                    <Link
                      to={`/crm-pro-evenementiel/prestations?id=${encodeURIComponent(p.id)}`}
                      className="px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 border bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25"
                      title="Ouvrir le dossier"
                    >
                      <FileText size={11} strokeWidth={1.5} /> Ouvrir
                    </Link>
                    {(() => {
                      const n = historyMap[p.id]?.length ?? 0;
                      return (
                        <button onClick={() => setHistoryModal({ prestId: p.id })} className="px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 border bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
                          <History size={11} strokeWidth={1.5} /> Historique{n > 0 ? ` (${n})` : ''}
                        </button>
                      );
                    })()}
                    <button onClick={() => setEditing({ prestId: p.id })} className="px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 border bg-violet-500/15 border-violet-500/40 text-violet-300 hover:bg-violet-500/25">
                      <BellPlus size={11} strokeWidth={1.5} /> Rappel
                    </button>
                    {doneAt ? (
                      <button onClick={() => reopenRelance(p.id)} className="px-3 py-1 rounded-md text-xs flex items-center gap-1.5 border bg-slate-800 border-slate-700 text-slate-300">
                        <RotateCcw size={11} strokeWidth={1.5} /> Remettre à faire
                      </button>
                    ) : (
                      <button onClick={() => setDoneModal({ prestId: p.id })} className="px-3 py-1 rounded-md text-xs flex items-center gap-1.5 border bg-emerald-500/20 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30">
                        <CheckCircle2 size={11} strokeWidth={1.5} /> Fait
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {reminders.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-800 space-y-1.5">
                  {reminders.map((r) => {
                    const isDue = !r.done && r.date <= todayIso;
                    return (
                      <div key={r.id} className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md border ${r.done ? 'border-slate-800 bg-slate-900/40 text-slate-500 line-through' : isDue ? 'border-violet-500/40 bg-violet-500/10 text-violet-200' : 'border-slate-800 bg-slate-900/60 text-slate-300'}`}>
                        <AlarmClock size={12} strokeWidth={1.5} className="shrink-0" />
                        <span className="font-medium whitespace-nowrap">{formatDateFR(r.date, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                        <span className="opacity-50">·</span>
                        <span className="flex-1 truncate">{r.note || 'Rappel'}</span>
                        <button onClick={() => toggleReminderDone(p.id, r.id)} className="p-1 rounded hover:bg-slate-800 text-emerald-400" title={r.done ? 'Rouvrir' : 'Marquer comme fait'}>
                          {r.done ? <RotateCcw size={11} strokeWidth={1.5} /> : <CheckCircle2 size={11} strokeWidth={1.5} />}
                        </button>
                        <button onClick={() => setEditing({ prestId: p.id, reminder: r })} className="p-1 rounded hover:bg-slate-800 text-slate-400" title="Modifier">
                          <Pencil size={11} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => deleteReminder(p.id, r.id)} className="p-1 rounded hover:bg-slate-800 text-rose-400" title="Supprimer">
                          <Trash2 size={11} strokeWidth={1.5} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}

        {!filtered.length && (
          <Card className="p-12 text-center text-slate-500">Aucune relance dans ce filtre.</Card>
        )}
      </div>

      {editing && (
        <ReminderModal
          initial={editing.reminder}
          onClose={() => setEditing(null)}
          onSave={(r) => { upsertReminder(editing.prestId, r); setEditing(null); }}
        />
      )}

      {doneModal && (() => {
        const p = all.find((x) => x.id === doneModal.prestId);
        return (
          <DoneRelanceModal
            client={p?.client}
            libelle={p?.libelle}
            onClose={() => setDoneModal(null)}
            onSave={(note) => { markRelanceDone(doneModal.prestId, note); setDoneModal(null); }}
          />
        );
      })()}

      {historyModal && (() => {
        const p = all.find((x) => x.id === historyModal.prestId);
        const entries = historyMap[historyModal.prestId] || [];
        return (
          <HistoryRelancesModal
            subtitle={[p?.client, p?.libelle].filter(Boolean).join(' — ')}
            entries={entries}
            onClose={() => setHistoryModal(null)}
          />
        );
      })()}
    </div>
  );
}

function DoneRelanceModal({ client, libelle, onClose, onSave }: { client?: string; libelle?: string; onClose: () => void; onSave: (note: string) => void }) {
  const [note, setNote] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-100 flex items-center gap-2">
            <CheckCircle2 size={18} strokeWidth={1.5} className="text-emerald-300" />
            Relance effectuée
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-200"><X size={18} strokeWidth={1.5} /></button>
        </div>
        {(client || libelle) && (
          <div className="text-xs text-slate-400 mb-3">{client}{client && libelle ? ' — ' : ''}{libelle}</div>
        )}
        <div>
          <label className="text-xs text-slate-400 block mb-1">Note / compte-rendu (optionnel)</label>
          <textarea autoFocus value={note} onChange={(e) => setNote(e.target.value)} rows={4}
            placeholder="Ex.: laissé message vocal, rappel demandé en septembre, devis renvoyé\u2026"
            className="w-full px-3 py-2 rounded-md bg-slate-950 border border-slate-700 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/50" />
          <p className="text-[11px] text-slate-500 mt-1.5">Cette note sera ajoutée à l’historique de la prestation.</p>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-xs border border-slate-700 text-slate-300 hover:bg-slate-800">Annuler</button>
          <button onClick={() => onSave(note)} className="px-3 py-1.5 rounded-md text-xs border bg-emerald-500/20 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 flex items-center gap-1.5">
            <CheckCircle2 size={12} strokeWidth={1.5} /> Marquer fait
          </button>
        </div>
      </div>
    </div>
  );
}


// Re-export du modal partagé pour préserver l'usage local (<ReminderModal ... />).
const ReminderModal = SharedReminderModal;


